import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CodeOutlined, ExperimentOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { trimStringWithEllipsis } from "../../../../../../../util/string.js";
function renderGroupNameTitle(groupName) {
    return /*#__PURE__*/ _jsx("span", {
        children: groupName
    }, "group");
}
function renderKnownRegexpItem(option) {
    return {
        value: option.regex,
        label: /*#__PURE__*/ _jsxs("div", {
            style: {
                display: "flex",
                justifyContent: "left",
                fontSize: "smaller"
            },
            children: [
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        width: "15rem",
                        justifyContent: "left"
                    },
                    children: /*#__PURE__*/ _jsxs(Tooltip, {
                        title: option.regex,
                        children: [
                            /*#__PURE__*/ _jsx(CodeOutlined, {}),
                            " ",
                            trimStringWithEllipsis(option.regex, 25)
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        width: "25rem",
                        justifyContent: "left"
                    },
                    children: /*#__PURE__*/ _jsxs(Tooltip, {
                        title: option.explanation,
                        children: [
                            /*#__PURE__*/ _jsx(InfoCircleOutlined, {}),
                            " ",
                            trimStringWithEllipsis(option.explanation, 50)
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("span", {
                    style: {
                        justifyContent: "right"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(ExperimentOutlined, {}),
                        " ",
                        option.example
                    ]
                })
            ]
        })
    };
}
function findExampleFromGroups(regexp, groups) {
    if (groups && regexp) {
        const found = groups.map((group)=>{
            return group?.options?.map((option)=>findExampleFromEntry(regexp, option)).flat();
        }).flat().filter(Boolean).shift();
        return found;
    }
    return undefined;
}
function findExampleFromEntry(regexp, entry) {
    if (entry && regexp) {
        if (entry.regex === regexp) {
            return entry.example;
        }
    }
    return undefined;
}
function getKnownRegexpOptions(t) {
    const knownRegexpGroups = [
        {
            title: t("Alphanumerics"),
            options: [
                {
                    regex: "^\\d+$",
                    explanation: "Matches one or more digits from start to end.",
                    example: "12345"
                },
                {
                    regex: "^\\d{5,}$",
                    explanation: "Matches 5 or more digits from start to end.",
                    example: "123456"
                },
                {
                    regex: "^[A-Za-z0-9]{8,}$",
                    explanation: "Matches 8 or more alphanumeric characters from start to end.",
                    example: "AbCd1234"
                },
                {
                    regex: "^[a-f0-9]{32}$",
                    explanation: "Matches a 32-character hexadecimal string.",
                    example: "0123456789abcdef0123456789abcdef"
                },
                {
                    regex: "^[A-Za-z0-9]{32,32}$",
                    explanation: "Matches a 32-character alphanumeric string.",
                    example: "AbCd1234EFGH5678ijkl9012MNOP3456"
                },
                {
                    regex: "^[A-Za-z0-9]{8,8}-[A-Za-z0-9]{4,4}-[A-Za-z0-9]{4,4}-[A-Za-z0-9]{4,4}-[A-Za-z0-9]{12,12}$",
                    explanation: "Matches a UUID in the format of 8-4-4-4-12 alphanumeric characters separated by dashes.",
                    example: "f7c5b5d5-7a5b-4b1a-a5c5-25d7d5b5c5d7"
                },
                {
                    regex: "^\\w+$",
                    explanation: "Matches one or more word characters (letters, digits, or underscores) from start to end.",
                    example: "AbCd_1234"
                },
                {
                    regex: "^\\+?[1-9]\\d{1,14}$",
                    explanation: "This regular expression is used to validate a phone number. It matches the standard format of a phone number with the country code, area code, and phone number.",
                    example: "+4934567890"
                }
            ]
        },
        {
            title: t("URLs, Links, Internet"),
            options: [
                {
                    regex: "/^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$/",
                    explanation: "This regular expression is used to validate an email address. It matches the standard format of an email address with characters, numbers, special characters, and domain names",
                    example: "info@devowl.io"
                },
                {
                    regex: "^\\.?(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\\.)+([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$",
                    explanation: "Matches a domain name with or without a leading dot.",
                    example: "example.com"
                },
                {
                    regex: "^http[s]?:\\/\\/.*[^\\/]",
                    explanation: "Matches a URL starting with 'http://' or 'https://' and ending before the first slash.",
                    example: "https://www.example.com"
                },
                {
                    regex: "^\\.?(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\\.)+([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])(\\/[^#\\?]*)?$",
                    explanation: "Matches a URL (with or without a leading dot) with optional path and query parameters.",
                    example: "https://www.example.com/path?query=param"
                }
            ]
        },
        {
            title: t("Tracking"),
            options: [
                {
                    regex: "^G-\\w{10,}$",
                    explanation: "Matches a Google Ads conversion tracking ID starting with 'G-' followed by 10 or more alphanumeric characters.",
                    example: "G-1234567890"
                },
                {
                    regex: "^AW-\\d{7,12}$",
                    explanation: "Matches an AdWords conversion tracking ID starting with 'AW-' followed by 7 to 12 digits.",
                    example: "AW-123456789"
                },
                {
                    regex: "^GTM-(.+)$",
                    explanation: "Matches a Google Tag Manager container ID starting with 'GTM-' followed by one or more characters.",
                    example: "GTM-ABCD123"
                },
                {
                    regex: "^UA-\\d+-\\d+$",
                    explanation: "Matches a Google Analytics tracking ID in the format of 'UA-123456-1'.",
                    example: "UA-123456-1"
                }
            ]
        }
    ];
    return knownRegexpGroups;
}
export { getKnownRegexpOptions, findExampleFromEntry, findExampleFromGroups, renderGroupNameTitle, renderKnownRegexpItem };
